'use client';

import COS, { UploadFileItemParams } from 'cos-js-sdk-v5';
import { post } from '@lib/http';
import { useState } from 'react';
import { message } from '@tencent/met-ui';
import { chooseUploadFile } from 'src/utils/upload-cos';
import { UploadFileResult, UploadOptions, UploadTokenData } from './types';

export const MAX_UPLOAD_COUNT = 4;
export const MAX_UPLOAD_SIZE = 1024 * 1024 * 10;
export const IS_SERVER_SIDE = typeof window === 'undefined';
export const ACCEPT_FILE_TYPES = 'image/png,image/jpeg,image/jpg,image/bmp';

export function getEventIdFromLocation(): string {
  if (IS_SERVER_SIDE) {
    return '';
  }
  const result = location.pathname.match(/\/event\/(\w+)\/registration/);
  const [, eventId] = result || [];
  return eventId || '';
}

export function getEventPageUrl() {
  if (IS_SERVER_SIDE) {
    return '';
  }
  const { pathname, origin } = window.location;
  return `${origin}${pathname}`;
}

export function checkFiles(result: unknown) {
  if (!Array.isArray(result)) {
    const { code } = result as {
      code: number;
    };
    let msg;
    if (code === 1) {
      msg = `图片大小不能超过${MAX_UPLOAD_SIZE / 1024 / 1024}M`;
    } else if (code === 2) {
      msg = `图片格式为${ACCEPT_FILE_TYPES}`;
    }
    message.error({ content: msg });
    return false;
  }
  if (result.length > MAX_UPLOAD_COUNT) {
    message.error({ content: `图片数量不能超过${MAX_UPLOAD_COUNT}张` });
    return false;
  }
  return result;
}

export function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = function () {
      if (rd.result) {
        resolve(rd.result as string);
      }
      reject(rd.result);
    };
    rd.onerror = reject;
    rd.readAsDataURL(file);
  });
}

/**
 * 举报功能上传服务
 */
export class UploadService {
  eventId: string;

  constructor() {
    if (typeof window !== 'undefined') {
      this.eventId = getEventIdFromLocation();
    } else {
      this.eventId = '';
    }
  }

  public setEventId(eventId: string) {
    this.eventId = eventId;
  }

  /**
   * 上传单个文件
   */
  public async uploadFile(file: File, options?: UploadOptions): Promise<UploadFileResult> {
    if (!this.eventId) {
      this.eventId = getEventIdFromLocation();
    }
    const token = await this.getUploadToken({
      event_id: this.eventId,
    });
    if (!token) {
      return {
        file,
        error: '获取上传token失败',
      };
    }
    const cos = new COS({
      getAuthorization: (_, callback) => {
        callback({
          TmpSecretId: token.credentials.tmpSecretId,
          TmpSecretKey: token.credentials.tmpSecretKey,
          XCosSecurityToken: token.credentials.sessionToken,
          StartTime: token.startTime,
          ExpiredTime: token.expiredTime,
        });
      },
    });
    const resp = await cos
      .putObject({
        Bucket: token.bucket,
        Region: token.region,
        Key: token.key,
        StorageClass: 'STANDARD',
        Body: file,
        onProgress: (info) => {
          options?.onFileProgress?.(file, info.percent);
        },
      })
      .catch(() => ({ statusCode: 500 }));
    if (resp.statusCode !== 200) {
      return {
        file,
        loading: false,
        error: '上传失败',
        percent: 0,
      };
    }
    const base64 = await fileToDataUrl(file);
    return {
      file,
      loading: false,
      percent: 1,
      url: base64,
      cos_id: token.showFileUrl,
      cos_url: token.showFileUrl,
    };
  }

  /**
   * 上传多个文件
   */
  public async uploadFiles(files: File[], options?: UploadOptions) {
    if (!this.eventId) {
      this.eventId = getEventIdFromLocation();
    }
    const token = await this.getUploadToken({
      event_id: this.eventId,
    });
    if (!token) {
      return {
        error: '获取上传token失败',
      };
    }
    const cos = new COS({
      getAuthorization: (_, callback) => {
        callback({
          TmpSecretId: token.credentials.tmpSecretId,
          TmpSecretKey: token.credentials.tmpSecretKey,
          XCosSecurityToken: token.credentials.sessionToken,
          StartTime: token.startTime,
          ExpiredTime: token.expiredTime,
        });
      },
    });
    const uploadFiles: UploadFileItemParams[] = files.map((file) => ({
      Bucket: token.bucket,
      Region: token.region,
      Key: token.key,
      StorageClass: 'STANDARD',
      Body: file,
      onProgress: (info) => {
        options?.onFileProgress?.(file, info.percent);
      },
      onFileFinish: (err) => {
        options?.onFileFinish?.(file, !!err);
      },
    }));
    const resp = await cos.uploadFiles({
      files: uploadFiles,
    });
    return resp;
  }

  private async getUploadToken(params: { event_id: string }): Promise<UploadTokenData | null> {
    const resp = await post<any>(
      '/wemeet-tapi',
      '/v2/common-logic/cos/get-report-token',
      params,
    ).catch(() => Promise.resolve(null));
    if (resp && resp.code === 0 && resp.data) {
      return resp.data as UploadTokenData;
    }
    return null;
  }
}

export function useUploadFile() {
  const uploadService = new UploadService();
  const [filesState, setFilesState] = useState<UploadFileResult[]>([]);

  const updateFileResult = (fileResult: UploadFileResult) => {
    setFilesState((prev) => {
      const index = prev.findIndex((f) => f.file === fileResult.file);
      if (index === -1) {
        return prev;
      }
      const update = [...prev];
      update[index] = fileResult;
      return update;
    });
  };

  /**
   * 上传单个文件
   */
  const uploadFile = async (oldFile?: File) => {
    const result = await chooseUploadFile(false, MAX_UPLOAD_SIZE, ACCEPT_FILE_TYPES).catch(
      (e) => e as any,
    );
    if (!checkFiles(result)) {
      return;
    }
    const [newFile] = result;
    if (oldFile) {
      const newFiles = filesState.map((f) =>
        f.file === oldFile ? { ...f, file: newFile, loading: true } : f,
      );
      setFilesState(newFiles);
    }
    const resp = await uploadService.uploadFile(newFile, {
      onFileProgress: (file, percent) => {
        updateFileResult({
          file,
          percent,
          loading: true,
        });
      },
    });
    updateFileResult(resp);
  };

  /**
   * 批量上传文件
   */
  const uploadFiles = async () => {
    const result: File[] = await chooseUploadFile(true, MAX_UPLOAD_SIZE, ACCEPT_FILE_TYPES).catch(
      (e) => e as any,
    );
    if (!checkFiles(result)) {
      return;
    }
    const initState: UploadFileResult[] = result.map((file) => ({
      file,
      loading: true,
    }));
    setFilesState((prev) => [...prev, ...initState]);
    const promise = result.map((file) =>
      uploadService.uploadFile(file, {
        onFileProgress: (file, percent) => {
          updateFileResult({
            file,
            percent,
            loading: true,
          });
        },
      }),
    );
    const resp = await Promise.all(promise);
    resp.forEach((item) => {
      updateFileResult(item);
    });
  };

  /**
   * 批量删除文件
   */
  const deleteFiles = (file: File[]) => {
    const newFiles = filesState.filter((item) => !file.includes(item.file!));
    setFilesState(newFiles);
  };

  /**
   * 删除文件
   */
  const deleteFile = (file: File) => {
    const newFiles = filesState.filter((item) => item.file !== file);
    setFilesState(newFiles);
  };

  const retryUpload = async (file: File) => {
    const resp = await uploadService.uploadFile(file);
    updateFileResult(resp);
    if (resp.error) {
      message.error({ content: resp.error });
    }
  };

  return {
    filesState,
    uploadFiles,
    uploadFile,
    deleteFiles,
    deleteFile,
    retryUpload,
  };
}
